'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { Language } from '@/lib/i18n';

interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  avatarInitials: string;
  referralCode: string;
  role: 'USER' | 'ADMIN' | 'SUPER_ADMIN';
  status: 'ACTIVE' | 'SUSPENDED';
  languagePreference: Language;
  createdAt: string;
}

interface WalletData {
  availableBalance: number;
  totalDeposited: number;
  totalWithdrawn: number;
  totalEarnings: number;
  totalCommissions: number;
  accumulatedEarnings: number;
  todayEarnings: number;
  interest: number;
  investmentAmount: number;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  profile: UserProfile | null;
  wallet: WalletData | null;
  language: Language;
  setLanguage: (lang: Language) => void;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  refreshWallet: () => Promise<void>;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [language, setLanguageState] = useState<Language>('en');
  const supabase = createClient();

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('nm_language', lang);
    }
    // Persist to DB if user is logged in
    if (user) {
      supabase
        .from('user_profiles')
        .update({ language_preference: lang })
        .eq('id', user.id)
        .then(() => {});
    }
  }, [user, supabase]);

  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error || !data) return null;
      const p: UserProfile = {
        id: data.id,
        email: data.email,
        fullName: data.full_name,
        avatarInitials: data.avatar_initials || data.full_name?.slice(0, 2).toUpperCase() || 'U',
        referralCode: data.referral_code || '',
        role: data.role as 'USER' | 'ADMIN' | 'SUPER_ADMIN',
        status: data.status as 'ACTIVE' | 'SUSPENDED',
        languagePreference: (data.language_preference as Language) || 'en',
        createdAt: data.created_at,
      };
      setProfile(p);
      // Restore language preference
      const storedLang = typeof window !== 'undefined' ? localStorage.getItem('nm_language') as Language : null;
      setLanguageState(storedLang || p.languagePreference || 'en');
      return p;
    } catch {
      return null;
    }
  }, [supabase]);

  const fetchWallet = useCallback(async (userId: string): Promise<WalletData | null> => {
    try {
      const { data, error } = await supabase
        .from('wallet_data')
        .select('*')
        .eq('user_id', userId)
        .single();
      if (error || !data) {
        // Return zeros if no wallet found
        const zeros: WalletData = {
          availableBalance: 0, totalDeposited: 0, totalWithdrawn: 0,
          totalEarnings: 0, totalCommissions: 0, accumulatedEarnings: 0,
          todayEarnings: 0, interest: 0, investmentAmount: 0,
        };
        setWallet(zeros);
        return zeros;
      }
      const w: WalletData = {
        availableBalance: Number(data.available_balance) || 0,
        totalDeposited: Number(data.total_deposited) || 0,
        totalWithdrawn: Number(data.total_withdrawn) || 0,
        totalEarnings: Number(data.total_earnings) || 0,
        totalCommissions: Number(data.total_commissions) || 0,
        accumulatedEarnings: Number(data.accumulated_earnings) || 0,
        todayEarnings: Number(data.today_earnings) || 0,
        interest: Number(data.interest) || 0,
        investmentAmount: Number(data.investment_amount) || 0,
      };
      setWallet(w);
      return w;
    } catch {
      return null;
    }
  }, [supabase]);

  useEffect(() => {
    // Restore language from localStorage immediately
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('nm_language') as Language;
      if (stored === 'en' || stored === 'es') {
        setLanguageState(stored);
      }
    }

    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user ?? null);
      if (user) {
        fetchProfile(user.id);
        fetchWallet(user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session?.user) {
        // Verify user authenticity via getUser()
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user ?? null);
        if (user) {
          fetchProfile(user.id);
          fetchWallet(user.id);
        }
      } else {
        setUser(null);
        setProfile(null);
        setWallet(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
          role: 'USER',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setWallet(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const refreshWallet = async () => {
    if (user) await fetchWallet(user.id);
  };

  const isAdmin = () => {
    return profile?.role === 'ADMIN' || profile?.role === 'SUPER_ADMIN';
  };

  const value: AuthContextType = {
    user, session, loading, profile, wallet, language,
    setLanguage, signUp, signIn, signOut, getCurrentUser,
    isEmailVerified, getUserProfile, refreshProfile, refreshWallet, isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
